/**
 * Safe UUID generation utility that provides fallback for environments
 * where crypto.randomUUID() is not available (older browsers, Node.js versions, etc.)
 */

/**
 * Generates a RFC4122 compliant UUID v4
 * Uses crypto.randomUUID() when available, falls back to custom implementation
 */
export function generateUUID(): string {
  // Check if crypto.randomUUID is available (modern browsers and Node.js 15.6+)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('crypto.randomUUID() failed, falling back to custom implementation:', error);
    }
  }

  // Fallback implementation for older environments
  return fallbackUUID();
}

/**
 * Fallback UUID v4 implementation using Math.random()
 * Based on RFC4122 standard
 */
function fallbackUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Generates a crypto-secure UUID when possible
 * Uses crypto.getRandomValues() if available for better randomness
 */
export function generateSecureUUID(): string {
  // Check if crypto.randomUUID is available first
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('crypto.randomUUID() failed, trying crypto.getRandomValues():', error);
    }
  }

  // Try crypto.getRandomValues() for better randomness
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    try {
      return cryptoSecureUUID();
    } catch (error) {
      console.warn('crypto.getRandomValues() failed, falling back to Math.random():', error);
    }
  }

  // Final fallback to Math.random()
  return fallbackUUID();
}

/**
 * UUID v4 implementation using crypto.getRandomValues()
 * Provides cryptographically secure random numbers
 */
function cryptoSecureUUID(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);

  // Set version (4) and variant bits according to RFC4122
  array[6] = (array[6]! & 0x0f) | 0x40; // Version 4
  array[8] = (array[8]! & 0x3f) | 0x80; // Variant 10

  const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32)
  ].join('-');
}

/**
 * Simple UUID generator (alias for generateUUID)
 * Most commonly used function
 */
export const uuid = generateUUID;

/**
 * Check if the environment supports crypto.randomUUID
 */
export function supportsCryptoRandomUUID(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
}

/**
 * Check if the environment supports crypto.getRandomValues
 */
export function supportsCryptoGetRandomValues(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function';
}

/**
 * Get information about UUID generation capabilities
 */
export function getUUIDCapabilities() {
  return {
    cryptoRandomUUID: supportsCryptoRandomUUID(),
    cryptoGetRandomValues: supportsCryptoGetRandomValues(),
    fallbackOnly: !supportsCryptoRandomUUID() && !supportsCryptoGetRandomValues()
  };
}

// Default export
export default generateUUID;
